/**
 * Generic batched, retrying, backpressured server channel.
 *
 * # Why this exists
 *
 * CC-1a's `telemetryServerChannel` and CC-1b's `auditServerChannel` need the
 * IDENTICAL delivery machinery — batch, retry with exponential backoff,
 * in-flight identity pinning, backpressure eviction, and the auth-drop /
 * validation-drop / retry outcome taxonomy — differing ONLY in the event/batch
 * TYPE, the per-event + per-batch validators, and how the batch is built +
 * POSTed. This module is that one shared loop; each channel is a thin
 * specialization (config), so the reliability contract lives in exactly one
 * place instead of being cloned per surface.
 *
 * # Loss model (unchanged from CC-1a)
 *
 * An event leaves the queue ONLY after the server acknowledges ITS chunk
 * (`ok:true`), removed BY OBJECT IDENTITY (never by position), so events
 * enqueued during an in-flight POST are never mistaken for the acked ones. A
 * failed chunk stays queued and is retried on the next flush/timer, so
 * transient errors never lose data. There are THREE drop paths, all counted in
 * `stats.dropped` (observable, never silent):
 *   1. backpressure cap (`maxQueueSize`) — evicts the OLDEST non-in-flight
 *      event (in-flight events are never evicted, so the queue may briefly
 *      exceed the cap while a POST is outstanding);
 *   2. auth-boundary failure (401/403) — terminal-drops EVERY event that was
 *      queued when the failing POST started (the rejected in-flight chunk AND
 *      the unsent pre-auth backlog behind it, by identity via a per-POST
 *      snapshot), so an unauthenticated/forbidden session can never re-POST its
 *      pre-auth events later under a newly authenticated session; events
 *      enqueued mid-POST are NOT in the snapshot and survive;
 *   3. validation/version failure (422) — terminal-drops ONLY the offending
 *      chunk (the server rejected its shape — e.g. a rolling-deploy schema skew
 *      — so retrying re-POSTs an identical invalid batch forever and would
 *      head-of-line-block all later data) and ADVANCES the drain to the next
 *      chunk. A 413 (body too large) is NOT dropped whole here — the drain
 *      RE-CHUNKS (halves + retries the same head) since the records are
 *      individually valid, and only a lone record that still 413s after being
 *      isolated is terminal-dropped as irreducible;
 *   4. endpoint-absent failure (404/405) — terminal-drops the offending chunk
 *      then ADVANCES, exactly like (3): a newer web bundle talking to an older
 *      API during a rolling deploy POSTs a route that is not registered yet and
 *      gets a 404 (or a 405 if the path exists under a different method). That
 *      is a non-retryable "not deployed here" condition — retrying re-POSTs the
 *      same never-servable request forever and head-of-line-blocks all later
 *      data — so the chunk is dropped and the drain advances (each following
 *      chunk 404s and drops in turn, draining the queue instead of storming).
 */

export interface BatchFlushResult {
  ok: boolean;
  status?: number;
}

export interface BatchedServerChannelStats {
  /** Events currently waiting in the queue (not yet delivered). */
  queued: number;
  /** Events successfully POSTed to the server. */
  delivered: number;
  /** Events rejected by validation at `enqueue` (never queued). */
  rejected: number;
  /**
   * Events removed WITHOUT delivery: evicted by the backpressure cap
   * (oldest-first), terminal-dropped on an auth-boundary (401/403) failure,
   * terminal-dropped on a validation/version (422) failure (the offending chunk
   * only), or — after a 413 re-chunk has narrowed the body to a SINGLE record
   * that still 413s (irreducible) — that lone oversized record.
   */
  dropped: number;
  /** Successful chunk POSTs. */
  flushes: number;
  /** Chunk POSTs that exhausted all retries (events kept for next flush). */
  failedFlushes: number;
  /** Total retry attempts across all flushes. */
  retries: number;
}

/**
 * Caller-facing tuning knobs common to every channel specialization (telemetry,
 * audit, …). Extracted so each specialization's options interface references
 * this ONE declaration (`extends BatchedServerChannelTuning`) instead of
 * re-listing the fields — the reliability tuning surface lives in one place.
 */
export interface BatchedServerChannelTuning {
  /** Auto-flush once the queue reaches this many events. Default 25. */
  batchSize?: number;
  /** Max events per POST. Default 200, hard-clamped to `hardChunkCap`. */
  maxChunkSize?: number;
  /** Periodic background flush interval in ms. 0 disables the timer. Default 5000. */
  flushIntervalMs?: number;
  /** Per-chunk retry attempts after the first try. Default 4. */
  maxRetries?: number;
  /** Exponential backoff base in ms (delay = base * 2^attempt). Default 400. */
  baseRetryDelayMs?: number;
  /** Backpressure cap — oldest events are dropped past this. Default 2000. */
  maxQueueSize?: number;
  /** Injectable delay (tests pass a no-op to avoid real timers). */
  delay?: (ms: number) => Promise<void>;
  onError?: (error: unknown) => void;
}

export interface BatchedServerChannelConfig<TEvent, TBatch>
  extends BatchedServerChannelTuning {
  /**
   * Validate + normalize ONE event at enqueue time. Return the normalized event
   * to queue it, or `null` to reject it (counted in `stats.rejected`, never
   * queued). The normalized return lets a specialization enqueue the parsed
   * (schema-coerced) value rather than the raw input.
   */
  validateEvent: (event: TEvent) => TEvent | null;
  /** Build the wire batch object from a chunk of queued events. */
  buildBatch: (events: TEvent[]) => TBatch;
  /**
   * Defensive pre-send re-validation of the built batch. Return the validated
   * batch to POST it, or `null` to drop the chunk (a malformed chunk must not
   * wedge the drain loop forever). The batch is rebuilt from already-validated
   * events, so this normally passes.
   */
  validateBatch: (batch: TBatch) => TBatch | null;
  /** POST the batch; resolve `{ ok, status? }`. */
  transport: (batch: TBatch) => Promise<BatchFlushResult>;
  /** Absolute per-POST cap (the wire contract's `events` max). Default 500. */
  hardChunkCap?: number;
}

const DEFAULTS = {
  batchSize: 25,
  maxChunkSize: 200,
  hardChunkCap: 500,
  flushIntervalMs: 5000,
  maxRetries: 4,
  baseRetryDelayMs: 400,
  maxQueueSize: 2000,
} as const;

const realDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Outcome of a chunk POST after exhausting retries:
 *   - `delivered`       — server acked; remove the chunk as delivered.
 *   - `auth-drop`       — terminal 401/403; drop the whole auth-boundary
 *                         snapshot, never retried.
 *   - `validation-drop` — terminal 422 (bad SHAPE — a rolling-deploy schema
 *                         skew); drop ONLY this chunk, then advance.
 *   - `too-large`       — 413 (aggregate body over a proxy/Laravel size limit).
 *                         NOT terminal for a multi-record chunk: the records are
 *                         not intrinsically invalid, so the drain RE-CHUNKS
 *                         (halves the chunk and retries the same head) until the
 *                         body fits; only a SINGLE record that still 413s is
 *                         terminal-dropped (it cannot be reduced further).
 *   - `endpoint-drop`   — terminal 404/405 (route not deployed on this API);
 *                         drop ONLY this chunk, then advance — never retried.
 *   - `retry`           — transient; keep the chunk for the next flush.
 */
type BatchSendOutcome =
  | "delivered"
  | "auth-drop"
  | "validation-drop"
  | "too-large"
  | "endpoint-drop"
  | "retry";

export class BatchedServerChannel<TEvent, TBatch> {
  private readonly config: BatchedServerChannelConfig<TEvent, TBatch>;
  private readonly batchSize: number;
  private readonly maxChunkSize: number;
  private readonly maxRetries: number;
  private readonly baseRetryDelayMs: number;
  private readonly maxQueueSize: number;
  private readonly delay: (ms: number) => Promise<void>;
  private readonly onError?: (error: unknown) => void;

  private readonly queue: TEvent[] = [];
  /** Shared empty in-flight set — avoids a fresh allocation on every clear. */
  private static readonly NO_INFLIGHT: ReadonlySet<unknown> = new Set<unknown>();
  /**
   * The exact event OBJECTS of the chunk currently being POSTed (pinned by
   * identity, NOT by any `id` string). Backpressure must never evict these
   * (they would be lost on a later transport failure since the queue is the
   * only retry buffer), and the ack removes exactly this set by identity.
   * Object identity — not an id string — because a producer reset can REUSE an
   * event id; identity can never collide across a reset. Empty whenever no POST
   * is outstanding.
   */
  private inFlightEvents: ReadonlySet<TEvent> =
    BatchedServerChannel.NO_INFLIGHT as ReadonlySet<TEvent>;
  /**
   * Reference-counted multiset of the event OBJECTS pinned by an in-flight
   * `flushFinal` POST. Unlike `inFlightEvents` (a single set owned by the serial
   * `drain`), `flushFinal` fans out several chunks CONCURRENTLY and a repeated
   * soft-hide can re-pin a still-queued event under two final POSTs at once, so
   * this is a refcount (incremented per pinning chunk, decremented on settle,
   * deleted at zero) rather than a plain set. Backpressure must never evict an
   * event pinned here: the queue is the ONLY retry buffer, so evicting a record
   * mid final-POST would lose its sole copy on a transport failure — the exact
   * guarantee `inFlightEvents` gives the drain path.
   */
  private readonly finalInFlight = new Map<TEvent, number>();
  private inFlight: Promise<void> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  private delivered = 0;
  private rejected = 0;
  private dropped = 0;
  private flushes = 0;
  private failedFlushes = 0;
  private retries = 0;

  constructor(config: BatchedServerChannelConfig<TEvent, TBatch>) {
    this.config = config;
    this.batchSize = Math.max(1, config.batchSize ?? DEFAULTS.batchSize);
    const hardCap = Math.max(1, config.hardChunkCap ?? DEFAULTS.hardChunkCap);
    this.maxChunkSize = Math.max(
      1,
      Math.min(config.maxChunkSize ?? DEFAULTS.maxChunkSize, hardCap),
    );
    this.maxRetries = Math.max(0, config.maxRetries ?? DEFAULTS.maxRetries);
    this.baseRetryDelayMs = Math.max(
      0,
      config.baseRetryDelayMs ?? DEFAULTS.baseRetryDelayMs,
    );
    this.maxQueueSize = Math.max(
      this.batchSize,
      config.maxQueueSize ?? DEFAULTS.maxQueueSize,
    );
    this.delay = config.delay ?? realDelay;
    this.onError = config.onError;

    const interval = config.flushIntervalMs ?? DEFAULTS.flushIntervalMs;
    if (interval > 0 && typeof setInterval === "function") {
      this.timer = setInterval(() => {
        void this.flush();
      }, interval);
      // Don't keep a Node/test process alive purely for this channel.
      (this.timer as { unref?: () => void }).unref?.();
    }
  }

  /**
   * Validate + enqueue one event. Returns false (without throwing) if the event
   * fails validation — safe to call from a store subscription where a throw
   * would break unrelated subscribers.
   */
  enqueue(event: TEvent): boolean {
    if (this.disposed) return false;

    const normalized = this.config.validateEvent(event);
    if (normalized === null) {
      this.rejected += 1;
      return false;
    }

    // Backpressure: bound the NON-pinned portion of the queue by evicting the
    // oldest evictable event; counted, never silent. Events pinned by an
    // in-flight drain POST (`inFlightEvents`) OR an in-flight final-flush POST
    // (`finalInFlight`) are skipped (dropping either would lose data on a later
    // transport failure), so the queue may briefly hold up to maxQueueSize plus
    // the pinned chunks. The subtraction is a fast-path only; the real
    // terminator is `dropOldestEvictable` returning false once every event is
    // pinned (so an overlap double-counted here just stops the loop one event
    // early — safe, never evicts a pinned record).
    while (
      this.queue.length - this.inFlightEvents.size - this.finalInFlight.size >=
      this.maxQueueSize
    ) {
      if (!this.dropOldestEvictable()) break;
    }

    this.queue.push(normalized);

    if (this.queue.length >= this.batchSize) {
      void this.flush();
    }
    return true;
  }

  /** Force-flush the queue. Concurrent calls coalesce onto one in-flight run. */
  flush(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.drain().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /**
   * #479 F4 — LAST-CHANCE unload flush. The install site sets its `unloading`
   * flag (so the transport upgrades to a `keepalive: true` POST) and then calls
   * THIS, never `flush()`, on `pagehide` / `visibilitychange`→hidden.
   *
   * Why not `flush()`:
   *   1. When a normal periodic/threshold drain is already in flight, `flush()`
   *      returns that existing `inFlight` promise — a fetch issued BEFORE
   *      `unloading` was set, so it has NO `keepalive` flag and navigation can
   *      cancel it — and every remaining chunk merely coalesces onto it and never
   *      starts its own request.
   *   2. `drain()` awaits each chunk's response before starting the next, so once
   *      the document is being torn down (where a microtask continuation may never
   *      run) only the FIRST chunk is guaranteed to be initiated.
   *
   * `flushFinal` instead snapshots the queue up front and INITIATES a fresh POST
   * for every chunk SYNCHRONOUSLY, in one pass, without awaiting between chunks —
   * the transport reads the now-true `unloading` flag and issues each as a
   * keepalive request that survives teardown. Re-sending an event a pre-unload
   * non-keepalive request also carried is safe: each record holds a stable
   * `clientEventUid` and the ingest endpoints dedupe on it (a cancelled
   * non-keepalive attempt is thus recovered by the keepalive re-send, and a
   * double-delivery is idempotent). Best-effort — NO retry (there is no future to
   * retry in); delivered events are removed by identity as each POST settles. It
   * never mutates `inFlightEvents` (a concurrent `drain` ack is untouched), but it
   * (1) EXCLUDES only a concurrent drain's genuinely in-flight HEAD chunk
   * (`inFlightEvents` — the exact records a real, already-issued POST is awaiting
   * a response for) from the IMMEDIATE resend — that drain owns THEIR 401/403/419
   * auth-boundary outcome, and re-POSTing them concurrently under whatever
   * credentials are current at unload would persist a pre-auth event under a
   * possibly-changed identity. The UNSENT BACKLOG behind the head is deliberately
   * NOT excluded (round-20, #ReRQp): those records were never submitted to the
   * server in any form, so resending them now — under the CURRENT session, their
   * first and only submission attempt — carries the exact same (pre-existing,
   * accepted) risk as the plain "no concurrent drain" resend below, not the
   * REPEAT-submission misattribution point (1) guards the head against.
   * Excluding the backlog too (the original round-17/18 shape) made its ONLY
   * delivery path the best-effort recovery pass in point (4) — but a `pagehide`
   * handler cannot await anything, so if the page tears down before the excluded
   * drain's own (non-keepalive, cancellable) fetch ever settles, recovery never
   * fires and that backlog is lost outright, even though a keepalive request for
   * it could have been issued synchronously right here. Sending it immediately
   * removes that dependency entirely — it no longer needs the drain to settle at
   * all; (2) PINS each fan-out chunk in the separate `finalInFlight` refcount so
   * backpressure cannot evict a record mid final-POST (the queue is its only
   * retry copy); (3) applies drain's terminal-status taxonomy to each POST's
   * result (drop the auth snapshot on 401/403/419, drop the offending chunk on
   * 422/404/405, RE-CHUNK — halve and retry both halves — on a multi-record 413
   * exactly like `drain`'s adaptive re-chunking, terminal-dropping only an
   * irreducible single-record 413) so a soft-hide tab that later SURVIVES never
   * resends, on a subsequent flush, data the normal drain would have terminally
   * dropped; and (4) — since the excluded HEAD chunk owns a real in-flight fetch
   * that a real `pagehide` can cancel or strand (see point 2 above: only the
   * FIRST chunk of a multi-chunk drain is guaranteed to even be initiated before
   * teardown) — schedules a best-effort RECOVERY pass, chained on the drain's OWN
   * settlement, that resends the excluded head if the drain did NOT end up
   * terminally dropping it. This closes the gap between the two failure modes: if
   * the drain settles normally (delivered, or terminally auth-dropped), the
   * recovery pass finds nothing left to send (drain's own bookkeeping already
   * removed those events) and is a no-op; if the drain's continuation never runs
   * (page fully torn down) the recovery pass never fires either — no additional
   * harm beyond the head chunk's own pre-existing risk (point 2), and — unlike the
   * old shape — the (typically larger) unsent backlog is NEVER exposed to that
   * same risk, since it was already sent in the immediate pass above.
   *
   * The recovery step first checks whether THIS pass's own resend of the
   * non-excluded backlog already hit an auth-boundary wall (401/403/419): if so,
   * the CURRENT session is provably broken at unload, so blindly resending the
   * excluded (drain-owned) head under it would repeat the exact misattribution
   * point (1) exists to prevent (a pre-auth/forbidden event persisted under a
   * possibly-changed identity). In that case the recovery step CONDEMNS the
   * excluded head instead of resending it — the same auth-boundary drop `drain`
   * itself would apply, just resolved at the moment we learn the session is bad
   * rather than at the drain's own (possibly-never-settling) POST. Only when this
   * pass's own resend did NOT hit an auth wall does the recovery step fall
   * through to a normal `flushFinal()` re-run.
   *
   * The recovery pass is NOT part of this method's own returned promise (callers
   * that `await flushFinal()` observe only the immediate pass, matching a real
   * unload handler which cannot await anything either) — it is deliberately
   * fire-and-forget.
   */
  flushFinal(): Promise<void> {
    if (this.disposed || this.queue.length === 0) return Promise.resolve();
    // Capture BEFORE the immediate pass runs (which may itself await/settle and
    // clear `this.inFlight` if, e.g., no concurrent drain is even active).
    // Capturing the SNAPSHOT REFERENCE (not just its size) lets the recovery step
    // condemn exactly these events later, by identity, without re-reading a field
    // the drain may have already reset to empty by then. Only the drain's
    // genuinely in-flight HEAD chunk is excluded/recovered here — see point (1)
    // above for why the unsent backlog is deliberately NOT part of this set.
    const concurrentDrain = this.inFlight;
    const excludedSnapshot = this.inFlightEvents;
    const hadExclusion = excludedSnapshot.size > 0;
    const immediate = this.flushFinalPass();
    if (concurrentDrain && hadExclusion) {
      // Best-effort recovery, chained DIRECTLY on the excluded drain's own
      // settlement — see point (4) above. `drain()` never actually rejects (every
      // transport error is caught internally and folded into a "retry" outcome),
      // but both branches are handled defensively so a hypothetical future
      // rejection still triggers recovery instead of silently dropping it. Never
      // awaited by the caller, so a page that truly tears down before this fires
      // loses nothing it would not already have lost.
      const recover = (): void => {
        if (this.disposed) return;
        // Wait for THIS pass's own immediate resend to settle so we know whether
        // it hit an auth-boundary wall before deciding how to treat the excluded
        // snapshot — see the auth-failure paragraph above.
        void immediate.then((authDroppedAtUnload) => {
          if (this.disposed) return;
          if (authDroppedAtUnload) {
            // This pass's own resend of the non-excluded backlog already
            // 401/403/419'd — the session is provably broken at unload, so
            // resending the excluded (drain-owned) snapshot under it would repeat
            // the exact misattribution the exclusion exists to prevent. Condemn it
            // instead, by identity — idempotent if the drain already dropped
            // some/all of it itself (a member already gone from `queue` is simply
            // not re-found).
            this.dropped += this.removeByIdentity(excludedSnapshot);
            return;
          }
          void this.flushFinal();
        });
      };
      void concurrentDrain.then(recover, recover);
    }
    return immediate.then(() => undefined);
  }

  /**
   * One fan-out pass of `flushFinal`: snapshot the CURRENT queue minus whatever
   * is CURRENTLY excluded by a concurrent drain's genuinely in-flight HEAD chunk,
   * POST it in `maxChunkSize` chunks via `sendFinalChunk` (which owns any 413
   * re-chunking), and apply drain's terminal-status taxonomy to each chunk's
   * settlement. Split out of `flushFinal` so the best-effort recovery follow-up
   * (point 4 on `flushFinal`'s docstring) can re-run this exact logic against
   * whatever `inFlightEvents` looks like AFTER the drain that excluded it has
   * settled (normally empty by then, so the recovery pass simply resends the head
   * if the drain did not terminally drop it).
   *
   * Resolves to whether ANY chunk of THIS pass hit a terminal auth-boundary
   * failure (401/403/419) — `flushFinal`'s recovery step reads this to decide
   * whether the session is provably broken at unload, in which case a concurrent
   * drain's excluded head must be CONDEMNED rather than blindly resent (see
   * `flushFinal`'s docstring).
   */
  private flushFinalPass(): Promise<boolean> {
    // Snapshot the resend set ONCE, up front, excluding ONLY a concurrent
    // drain's genuinely in-flight HEAD chunk (`inFlightEvents` — the exact
    // records a real, already-issued POST is awaiting a response for). The
    // drain owns THAT chunk's 401/403/419 outcome; a final resend under
    // whatever credentials are current at unload would repeat a REPEAT
    // submission under a possibly-changed identity — the misattribution the
    // auth-boundary drop exists to prevent. The UNSENT BACKLOG behind the head
    // is deliberately NOT excluded (round-20, #ReRQp — see `flushFinal`'s
    // docstring point 1): it was never submitted anywhere, so this IS its first
    // submission attempt, carrying no more risk than the plain
    // no-concurrent-drain resend below. Capturing the array once also means a
    // concurrent drain ack that mutates `queue` mid-fan-out cannot skip or
    // double-send a record.
    const resendable =
      this.inFlightEvents.size === 0
        ? this.queue.slice()
        : this.queue.filter((event) => !this.inFlightEvents.has(event));
    if (resendable.length === 0) return Promise.resolve(false);
    // The final flush's OWN auth boundary: the events it is responsible for at
    // unload. If a keepalive POST returns a terminal 401/403/419 for a tab that
    // then SURVIVES (soft `visibilitychange`→hidden), this whole set must be
    // dropped — never left queued for a later timer/threshold flush to resend
    // under a subsequent session — exactly as `drain` drops its auth snapshot.
    // Shared (not re-derived) across every top-level chunk AND every recursive
    // 413 split of this pass: a 401/403/419 on ANY of them condemns the WHOLE
    // resendable set, not just the sub-chunk that happened to hit the wall.
    const finalAuthSnapshot = new Set(resendable);
    const pending: Promise<boolean>[] = [];
    for (
      let start = 0;
      start < resendable.length;
      start += this.maxChunkSize
    ) {
      const chunk = resendable.slice(start, start + this.maxChunkSize);
      pending.push(this.sendFinalChunk(chunk, finalAuthSnapshot));
    }
    return Promise.all(pending).then((results) => results.some((v) => v));
  }

  /**
   * POST one `flushFinal` chunk and apply drain's terminal-status taxonomy to
   * its settlement — extracted from `flushFinalPass` so a multi-record 413 can
   * RECURSE (halve the chunk and retry BOTH halves) exactly like `drain`'s
   * adaptive re-chunking, instead of leaving an aggregate-oversized-but-valid
   * chunk queued for "a later drain" that does not exist once the page has
   * actually unloaded (there is no timer/threshold flush after teardown to pick
   * it back up). Only an IRREDUCIBLE single-record 413 is terminal-dropped,
   * mirroring `drain`'s own `too-large` handling.
   *
   * The chunk is pinned (`pinFinal`) against backpressure eviction for the FULL
   * duration of this call, including any recursive 413 splits — `finally`
   * releases the pin only after both halves (if split) have settled, so a record
   * is never briefly unprotected mid-halving. The pin is refcounted (see
   * `pinFinal`), so a half re-pinning its own subset while still covered by the
   * parent's pin is safe (and expected) double-counting.
   *
   * Returns whether THIS chunk (or either of its recursive halves) hit a
   * terminal auth-boundary failure (401/403/419) — `flushFinalPass` folds this
   * across every chunk of the pass to report back to `flushFinal`.
   */
  private sendFinalChunk(
    chunk: TEvent[],
    finalAuthSnapshot: ReadonlySet<TEvent>,
  ): Promise<boolean> {
    const validated = this.config.validateBatch(this.config.buildBatch(chunk));
    if (validated === null) {
      this.dropped += this.removeByIdentity(new Set(chunk));
      return Promise.resolve(false);
    }
    const inFlight = new Set(chunk);
    // Pin the chunk against backpressure eviction BEFORE initiating the POST —
    // during teardown the queue is the only retry copy, so an evict-then-fail
    // would lose the record. Released when the POST (and any recursive 413
    // split) settles (finally, below).
    this.pinFinal(chunk);
    // Initiate the POST SYNCHRONOUSLY — the transport call itself must run
    // before the document is torn down; only the settle-time bookkeeping below
    // is deferred to a microtask.
    let sent: Promise<BatchFlushResult>;
    try {
      sent = this.config.transport(validated);
    } catch (error) {
      this.unpinFinal(chunk);
      this.failedFlushes += 1;
      this.onError?.(error);
      return Promise.resolve(false);
    }
    return sent
      .then((result): boolean | Promise<boolean> => {
        if (result.ok) {
          this.delivered += this.removeByIdentity(inFlight);
          this.flushes += 1;
          return false;
        }
        // Non-OK: honor the SAME terminal-status taxonomy as `drain` so a
        // soft-hide tab that later SURVIVES never lets a subsequent
        // timer/threshold flush resend data the normal drain would have
        // terminally dropped.
        this.failedFlushes += 1;
        if (
          result.status === 401 ||
          result.status === 403 ||
          result.status === 419
        ) {
          // Auth-boundary terminal: drop the WHOLE final-flush snapshot by
          // identity so pre-auth/forbidden events are never re-POSTed under a
          // later, newly-authenticated session — the misattribution `drain`
          // prevents by dropping its auth-boundary snapshot. Idempotent across
          // the concurrent fan-out: a sibling chunk that already settled has
          // been removed, so a second drop finds nothing.
          this.dropped += this.removeByIdentity(finalAuthSnapshot);
          return true;
        }
        if (
          result.status === 422 ||
          result.status === 404 ||
          result.status === 405
        ) {
          // Terminal chunk rejection — bad SHAPE (422) or absent route
          // (404/405): a resend can never succeed and would head-of-line-
          // block later data, so drop ONLY this chunk (like drain's
          // validation-drop / endpoint-drop) and leave the rest queued.
          this.dropped += this.removeByIdentity(inFlight);
          return false;
        }
        if (result.status === 413 && chunk.length > 1) {
          // 413 — the AGGREGATE body exceeded a proxy/Laravel request-size
          // limit, but the records are not intrinsically invalid. Unlike the
          // normal `drain`, this pass has no later flush to lean on once the
          // page has actually unloaded, so RE-CHUNK right here: halve the chunk
          // and retry BOTH halves (each re-pins its own subset — see the
          // docstring above — so nothing is briefly unprotected). Converges in
          // O(log chunkSize) recursive rounds, same as drain's halving.
          const mid = Math.floor(chunk.length / 2);
          return Promise.all([
            this.sendFinalChunk(chunk.slice(0, mid), finalAuthSnapshot),
            this.sendFinalChunk(chunk.slice(mid), finalAuthSnapshot),
          ]).then((results) => results.some((v) => v));
        }
        if (result.status === 413) {
          // A single record that still 413s cannot be reduced further —
          // terminal-drop it (mirrors drain's irreducible-413 handling) instead
          // of leaving it queued for a later flush that will not happen once
          // the page has actually torn down.
          this.dropped += this.removeByIdentity(inFlight);
          return false;
        }
        // Transient (5xx / network) — keep the record queued for a later flush
        // (a soft-hide tab that survives may still get one).
        return false;
      })
      .catch((error) => {
        this.failedFlushes += 1;
        this.onError?.(error);
        return false;
      })
      .finally(() => {
        this.unpinFinal(chunk);
      });
  }

  /** Resolves when any in-flight flush settles (no-op if idle). */
  idle(): Promise<void> {
    return this.inFlight ?? Promise.resolve();
  }

  size(): number {
    return this.queue.length;
  }

  stats(): BatchedServerChannelStats {
    return {
      queued: this.queue.length,
      delivered: this.delivered,
      rejected: this.rejected,
      dropped: this.dropped,
      flushes: this.flushes,
      failedFlushes: this.failedFlushes,
      retries: this.retries,
    };
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async drain(): Promise<void> {
    // Adaptive per-drain chunk ceiling. Starts at the configured `maxChunkSize`
    // and only SHRINKS — a 413 (aggregate body too large) halves it so the SAME
    // head is retried as a smaller body instead of dropping the whole burst
    // (records that are individually valid must not be lost to an aggregate size
    // limit). Monotonic non-increasing within a drain (never re-grown, to avoid
    // oscillating back into the same 413); the next drain resets it here.
    let chunkLimit = this.maxChunkSize;
    while (this.queue.length > 0) {
      const chunk = this.queue.slice(0, Math.min(chunkLimit, this.queue.length));
      // Pin the EXACT event objects (by identity, not by any `id` string) so the
      // ack and the backpressure guard reference the same references in the
      // queue. Identity is mandatory because a producer reset can reuse an
      // in-flight id on a fresh, never-sent event; removing by id would evict
      // that fresh event the instant the pre-reset chunk acks.
      const inFlight = new Set(chunk);
      // Auth-boundary snapshot: EVERY event already queued when this POST starts
      // (the chunk AND the unsent backlog tail beyond it) was recorded
      // at-or-before the moment the server evaluates this session's auth. A
      // 401/403 therefore condemns the whole snapshot, not just the rejected
      // chunk. Events enqueued WHILE the POST is pending (possibly post-login)
      // are never members.
      const authBoundarySnapshot = new Set(this.queue);
      const batch = this.config.buildBatch(chunk);

      // Defensive: the batch is rebuilt from already-validated events, but a
      // malformed chunk must not wedge the loop forever.
      const validated = this.config.validateBatch(batch);
      if (validated === null) {
        this.dropped += this.removeByIdentity(inFlight);
        continue;
      }

      // Pin the chunk as in-flight so concurrent backpressure (enqueue can run
      // while this POST awaits) never evicts an event we're about to ack, and so
      // a concurrent unload `flushFinal` excludes exactly this genuinely
      // in-flight chunk from its resend (round-20, #ReRQp: the unsent backlog
      // behind it is deliberately NOT excluded there — see `flushFinal`'s
      // docstring). `authBoundarySnapshot` (below) stays a LOCAL var: it is
      // `drain`'s own bookkeeping for ITS 401/403/419 condemnation, never read by
      // a concurrent `flushFinal`.
      this.inFlightEvents = inFlight;
      const outcome = await this.sendWithRetry(validated);
      this.inFlightEvents =
        BatchedServerChannel.NO_INFLIGHT as ReadonlySet<TEvent>;

      if (outcome === "delivered") {
        // Remove exactly the acked events BY IDENTITY — never positionally:
        // events enqueued during the in-flight POST (and any backpressure shift
        // of the head) must not be miscounted as delivered.
        this.delivered += this.removeByIdentity(inFlight);
        this.flushes += 1;
      } else if (outcome === "auth-drop") {
        // Auth-boundary failure (401/403): terminal-drop EVERY event that was
        // queued when the failing POST started — the rejected in-flight chunk
        // AND the unsent pre-auth backlog behind it — by identity. Events
        // enqueued WHILE this POST was pending are NOT in the snapshot and
        // survive for the next attempt.
        this.failedFlushes += 1;
        this.dropped += this.removeByIdentity(authBoundarySnapshot);
        break;
      } else if (outcome === "too-large") {
        // 413 — the AGGREGATE body exceeded a proxy/Laravel request-size limit.
        // The records are not intrinsically invalid, so split instead of drop:
        //   • chunk > 1 record: HALVE the ceiling and retry the SAME head (never
        //     drop, never advance) — a smaller body of the same records usually
        //     fits. Repeated halving converges in O(log chunkSize) round-trips.
        //   • chunk == 1 record: a lone record that still 413s cannot be reduced
        //     further, so terminal-drop it (like a 422) and advance — otherwise
        //     it would head-of-line-block every later event forever.
        this.failedFlushes += 1;
        if (chunk.length > 1) {
          chunkLimit = Math.max(1, Math.floor(chunk.length / 2));
          continue;
        }
        this.dropped += this.removeByIdentity(inFlight);
        continue;
      } else if (outcome === "validation-drop" || outcome === "endpoint-drop") {
        // Terminal, non-retryable rejection of THIS chunk — CONTINUE to the next
        // chunk. Two causes share this path (413 is handled ABOVE by re-chunking,
        // never here):
        //   • 422 (validation-drop): the server rejected this batch's SHAPE
        //     (a rolling-deploy schema-version skew or any contract reject).
        //     Chunk-specific, not session-wide, so the unsent backlog is NOT
        //     condemned — a same-shape tail chunk 422s and drops in turn.
        //   • 404/405 (endpoint-drop): the route is not deployed on this API
        //     (a newer web bundle against an older backend). Endpoint-wide, so
        //     every following chunk 404s and drops in turn — which drains the
        //     queue rather than storming the missing route forever.
        // Either way: retrying re-POSTs an identical request that can never
        // succeed and would head-of-line-block all later data, so drop ONLY the
        // offending in-flight chunk by identity and advance. Events enqueued
        // mid-POST are not in `inFlight` and survive.
        this.failedFlushes += 1;
        this.dropped += this.removeByIdentity(inFlight);
        continue;
      } else {
        // Transient failure — keep the chunk (and the rest of the queue) for the
        // next attempt; no event is dropped on a recoverable error.
        this.failedFlushes += 1;
        break;
      }
    }
  }

  /**
   * Remove every queued event whose OBJECT IDENTITY is in `events`, preserving
   * order and the stable `queue` array reference. Returns the count removed.
   */
  private removeByIdentity(events: ReadonlySet<TEvent>): number {
    let write = 0;
    for (let read = 0; read < this.queue.length; read += 1) {
      const event = this.queue[read]!;
      if (!events.has(event)) {
        this.queue[write] = event;
        write += 1;
      }
    }
    const removed = this.queue.length - write;
    this.queue.length = write;
    return removed;
  }

  /**
   * Evict the OLDEST event that is not currently pinned by an in-flight drain
   * POST (`inFlightEvents`) OR an in-flight final-flush POST (`finalInFlight`)
   * — backpressure. Returns false when every queued event is pinned (nothing
   * safe to drop) so the caller stops looping instead of spinning forever.
   */
  private dropOldestEvictable(): boolean {
    for (let index = 0; index < this.queue.length; index += 1) {
      const event = this.queue[index]!;
      // Skip events pinned by an in-flight drain POST OR an in-flight final-flush
      // POST — evicting either would drop the sole retry copy of a record still
      // being delivered.
      if (!this.inFlightEvents.has(event) && !this.finalInFlight.has(event)) {
        this.queue.splice(index, 1);
        this.dropped += 1;
        return true;
      }
    }
    return false;
  }

  /**
   * Pin every event in `chunk` as final-flush-in-flight (refcount +1 each) so
   * backpressure skips it while its `flushFinal` POST is outstanding. Refcounted
   * (not a plain set) because the fan-out is concurrent and a repeated soft-hide
   * can pin the same still-queued event under two final POSTs at once.
   */
  private pinFinal(chunk: readonly TEvent[]): void {
    for (const event of chunk) {
      this.finalInFlight.set(event, (this.finalInFlight.get(event) ?? 0) + 1);
    }
  }

  /** Release a final-flush pin (refcount −1 each; the entry is deleted at zero). */
  private unpinFinal(chunk: readonly TEvent[]): void {
    for (const event of chunk) {
      const count = this.finalInFlight.get(event);
      if (count === undefined) continue;
      if (count <= 1) this.finalInFlight.delete(event);
      else this.finalInFlight.set(event, count - 1);
    }
  }

  private async sendWithRetry(batch: TBatch): Promise<BatchSendOutcome> {
    for (let attempt = 0; attempt <= this.maxRetries; attempt += 1) {
      try {
        const result = await this.config.transport(batch);
        if (result.ok) return "delivered";
        // Auth-boundary failures are TERMINAL, never transient: a 401/403 means
        // the session is unauthenticated/forbidden, so retrying (or a later
        // flush) would only re-POST the same pre-auth events — possibly under a
        // DIFFERENT, newly authenticated session. A 419 is the SAME boundary:
        // the ingest routes sit behind Laravel's `web` CSRF guard
        // (`Route::middleware('web')` in routes/api.php; `validateCsrfTokens`
        // exempts only logout), so an expired / missing XSRF token yields a
        // TokenMismatch 419. Retrying re-POSTs the identical chunk with the same
        // stale token — it can never pass until a reload mints a fresh token, so
        // it would head-of-line-block every later audit / telemetry event.
        // Drop the pre-auth snapshot like the other auth failures, don't retry.
        if (
          result.status === 401 ||
          result.status === 403 ||
          result.status === 419
        ) {
          return "auth-drop";
        }
        // A 422 (Unprocessable) is TERMINAL: the server rejected THIS batch's
        // SHAPE (a rolling-deploy schema-version skew or any contract reject), so
        // retrying re-POSTs the identical invalid body forever and head-of-line-
        // blocks all later data — drop ONLY the offending chunk and ADVANCE.
        if (result.status === 422) {
          return "validation-drop";
        }
        // A 413 (Payload Too Large) is DIFFERENT: the AGGREGATE body crossed a
        // proxy/Laravel request-size limit, but the individual records are not
        // intrinsically invalid — a smaller chunk of the SAME records is usually
        // accepted (e.g. several valid audit records each carrying a large capped
        // `entityIds` list, batched together by the default 200-event chunk).
        // Re-POSTing the identical oversized body is futile, so this is not a
        // plain `retry`; the drain re-chunks (halves + retries the same head)
        // instead of dropping the whole burst. A single record that still 413s is
        // dropped there (it cannot be reduced further).
        if (result.status === 413) {
          return "too-large";
        }
        // Endpoint-absent responses are TERMINAL too: a 404 (route not
        // registered) or 405 (path exists under a different method) means a
        // newer web bundle is talking to an older API during a rolling deploy
        // that has not deployed this ingest route yet. Retrying re-POSTs a
        // request the server can never serve, storming the missing route and
        // head-of-line-blocking all later data. Drop the chunk and ADVANCE.
        if (result.status === 404 || result.status === 405) {
          return "endpoint-drop";
        }
      } catch (error) {
        this.onError?.(error);
      }

      if (attempt < this.maxRetries) {
        this.retries += 1;
        await this.delay(this.baseRetryDelayMs * 2 ** attempt);
      }
    }
    return "retry";
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Shared idempotency + fetch delivery (used by both the telemetry and audit
// specializations — one credentialed-POST implementation, not two clones)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Mint a collision-resistant per-event identifier — the server-side dedupe key
 * (`clientEventUid`). `crypto.randomUUID` is the primary source (every
 * secure-context browser + Node/jsdom); the timestamp+random fallback covers
 * non-secure contexts / older runtimes so a missing `crypto.randomUUID` never
 * produces a colliding key. Generated ONCE per projected event (never re-minted
 * on a channel retry), so a true retry keeps its uid (server dedupes) while two
 * distinct events — even a reset-reused `audit-N` id at the same ms — differ.
 */
export function generateClientEventUid(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `evt-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

/**
 * Conservative per-request byte budget for a `keepalive` unload POST.
 *
 * The Fetch spec caps the TOTAL body size of all in-flight `keepalive` requests
 * in a context at ~64 KB. The telemetry AND audit unload flushes both draw on
 * that ONE shared budget, so each channel bounds its own keepalive TRAFFIC to a
 * fraction of 64 KB — 32 KB — guaranteeing that even if both fire at-budget
 * simultaneously the combined 64 KB is not exceeded. This is BOTH a per-request
 * cap AND — because `flushFinal` fans out several chunks synchronously — a
 * per-channel AGGREGATE cap: the transport tallies its in-flight keepalive bytes
 * and stops opting new chunks into keepalive once the 32 KB share is committed. A
 * request that would breach either cap makes a `keepalive: true` fetch REJECT, and
 * during teardown there is no retry, so the transport falls back to a non-keepalive
 * POST for that chunk (best-effort completion) rather than a guaranteed rejection.
 */
export const KEEPALIVE_BODY_BUDGET_BYTES = 32 * 1024;

/**
 * UTF-8 byte length of `body`, for the keepalive budget check. Uses `TextEncoder`
 * (universal in browsers + jsdom + Node) so a multibyte body (emoji, CJK) is
 * measured by its real wire size, not its UTF-16 code-unit count. Only invoked on
 * the rare unload flush, so the per-call allocation is not on the steady path.
 */
function utf8ByteLength(body: string): number {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(body).length;
  }
  // Fallback for a runtime without `TextEncoder` (none of the supported browser
  // / jsdom / Node targets — the encoder is universal): every UTF-16 code unit
  // is at least one UTF-8 byte, so the code-unit count is a lower bound on the
  // true byte size. Multiply by 3 (the max bytes for a BMP code unit) for a
  // conservative UPPER estimate, so an oversized multibyte body is not mistaken
  // for one that fits the keepalive budget.
  return body.length * 3;
}

/** Read Laravel's `XSRF-TOKEN` cookie (mirrored into the `X-XSRF-TOKEN` header). */
export function readXsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/u);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export interface FetchBatchTransportOptions {
  /**
   * Backend origin. `''` is the same-origin root (normalized
   * `VITE_API_BASE_URL='/'`) — a valid config that builds a relative
   * `/api/v1/...` POST. The `undefined`-means-no-backend decision is the
   * CALLER's (each specialization returns a no-op transport in that case), so
   * this helper always POSTs.
   */
  baseUrl: string;
  path: string;
  fetchImpl?: typeof fetch;
  /**
   * Unload-flush hook. When this predicate returns true (the install site sets
   * it on `pagehide` / `visibilitychange`→hidden), the POST is issued with
   * `keepalive: true` so the browser completes the request even as the document
   * is torn down. This is the modern replacement for `navigator.sendBeacon`,
   * which cannot be used here: the `web`-guarded ingest route requires the CSRF
   * `X-XSRF-TOKEN` header and a JSON body, neither of which `sendBeacon`
   * carries. Steady-state POSTs stay non-keepalive (the 64 KB keepalive budget
   * is reserved for the small final unload flush, not full 500-event chunks).
   */
  keepalive?: () => boolean;
}

/**
 * A credentialed JSON POST to a Laravel ingest endpoint. Credentials are
 * included so the session cookie (and Laravel's `XSRF-TOKEN`, mirrored into the
 * `X-XSRF-TOKEN` header) authenticate the request the same way the rest of the
 * session-aware API surface does. Shared by the telemetry + audit channels so
 * the delivery contract (headers / credentials / result mapping) lives once.
 */
export function createFetchBatchTransport<TBatch>(
  options: FetchBatchTransportOptions,
): (batch: TBatch) => Promise<BatchFlushResult> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const url = `${options.baseUrl.replace(/\/+$/u, "")}${options.path}`;

  // Running total of the UTF-8 bytes of this transport's CURRENTLY in-flight
  // keepalive requests. `flushFinal` fans out every queued chunk SYNCHRONOUSLY
  // (no await between chunks), so N chunks each individually ≤
  // KEEPALIVE_BODY_BUDGET_BYTES could still SUM past this channel's share of the
  // browser's shared ~64 KB keepalive quota and make the later fetches REJECT
  // (with no retry during teardown). This ledger caps the AGGREGATE keepalive
  // bytes the transport commits to its per-channel budget: once the budget is
  // spent, the remaining fan-out chunks fall back to best-effort non-keepalive
  // POSTs instead of piling onto — and overflowing — the shared quota. The
  // increment is synchronous (before the awaited fetch), so each chunk in the
  // fan-out sees the bytes already committed by the earlier chunks. Decremented
  // when each keepalive fetch settles: a soft `visibilitychange`→hidden the page
  // then survives frees the quota for a later hide, while a real unload never runs
  // the settle — correct, the browser holds that quota until the request drains.
  let keepaliveBytesInFlight = 0;

  return async (batch: TBatch): Promise<BatchFlushResult> => {
    const headers: Record<string, string> = {
      "content-type": "application/json",
      accept: "application/json",
    };
    const xsrf = readXsrfToken();
    if (xsrf) headers["x-xsrf-token"] = xsrf;

    // Serialize once — reused for the keepalive budget check AND the request body.
    const body = JSON.stringify(batch);
    // The unload predicate gates EVERYTHING below, including the byte-length scan:
    // only the rare unload flush needs the UTF-8 byte count (the keepalive budget
    // check), so evaluate it ONCE and skip `utf8ByteLength` entirely on the
    // steady-state path. Without this gate, every telemetry/audit POST — including
    // large steady-state audit batches that already pay for `JSON.stringify` — paid
    // for a second full-body `TextEncoder` allocation/scan whose result was never
    // used (`options.keepalive?.()` false made `useKeepalive` false regardless of
    // `bodyBytes`).
    const isUnloadFlush = options.keepalive?.() === true;
    const bodyBytes = isUnloadFlush ? utf8ByteLength(body) : 0;
    // Only the unload flush opts into keepalive (see FetchBatchTransportOptions),
    // and only when the body fits the per-request keepalive byte budget AND still
    // fits ALONGSIDE the keepalive bytes this transport already has in flight: a
    // body over the shared ~64 KB keepalive quota makes a `keepalive: true` fetch
    // REJECT, and during teardown there is no retry to recover it. Both an
    // oversized single chunk AND an aggregate fan-out that would breach this
    // channel's budget therefore fall back to a best-effort non-keepalive POST
    // rather than a guaranteed keepalive rejection.
    const useKeepalive =
      isUnloadFlush &&
      bodyBytes <= KEEPALIVE_BODY_BUDGET_BYTES &&
      keepaliveBytesInFlight + bodyBytes <= KEEPALIVE_BODY_BUDGET_BYTES;

    if (useKeepalive) keepaliveBytesInFlight += bodyBytes;
    try {
      const response = await fetchImpl(url, {
        method: "POST",
        credentials: "include",
        headers,
        body,
        ...(useKeepalive ? { keepalive: true } : {}),
      });
      return { ok: response.ok, status: response.status };
    } finally {
      if (useKeepalive) keepaliveBytesInFlight -= bodyBytes;
    }
  };
}
